/**
 * Copyright 2026 Perform Content Services Limited. Confidential and Proprietary. All Rights Reserved.
 */

import GlobalScope from '../../globalScope/GlobalScope';
import DiscoveryService from './DiscoveryService';
import EndPoint from './EndPoint';
import SDK from '../SDK';

const discoveryCacheInterval = 60000;

interface IPerURIEndpoint { [x: string]: Promise<EndPoint> }

export default class Discovery {
  private static _cache: IPerURIEndpoint = {};

  static async precacheClosestEndPointDiscovery(): Promise<EndPoint> {
    return new Promise<EndPoint>(resolveEndPointPromise => {
      const globalScopeSubscription = GlobalScope.subscribe(globalScope => {
        if (!globalScope) {
          return;
        }

        const uri = new globalScope.URL(SDK.discoveryUri.value);

        return Discovery.discoverClosestEndPointWithCaching(uri)
          .then(resolveEndPointPromise)
          .then(() => globalScopeSubscription.dispose());
      });
    });
  }

  static async discoverClosestEndPointWithCaching(uri: URL): Promise<EndPoint> {
    const url = uri.toString();

    if (url in Discovery._cache) {
      return Discovery._cache[url];
    }

    const discoveryService = new DiscoveryService(uri);
    const cachedValue = Discovery._cache[url] = discoveryService.discoverClosestEndPoint();
    const ignored = Discovery._cache[url].then(() => {
      const ignored = GlobalScope.value.setTimeout(() => {
        if (Discovery._cache[url] === cachedValue) {
          delete Discovery._cache[url];
        }
      }, discoveryCacheInterval);
    }).catch(() => {
      delete Discovery._cache[url];
    });

    return cachedValue;
  }

  static clearCachedClosestEndpoint(uri: URL): void {
    const url = uri.toString();

    if (url in Discovery._cache) {
      delete Discovery._cache[url];
    }
  }

  private constructor() {
    throw new Error('Discovery is a static class that may not be instantiated');
  }
}

const ignored = Discovery.precacheClosestEndPointDiscovery();